export const MATCH_SCORE_REVIEW_THRESHOLDS = Object.freeze({
  strong: 81,
  medium: 50,
});

export const MATCH_SCORE_REVIEW_THRESHOLD = MATCH_SCORE_REVIEW_THRESHOLDS.strong;

const toId = (value) => (value == null ? "" : String(value));

const toScore = (value) => {
  const score = Number(value);
  return Number.isFinite(score) && score >= 0 && score <= 100 ? score : null;
};

export const getMatchScoreReviewMeta = (score) => {
  const pct = toScore(score);
  if (pct == null) return null;

  if (pct >= MATCH_SCORE_REVIEW_THRESHOLDS.strong) {
    return { key: "strong", label: "Strong Match", pct };
  }
  if (pct >= MATCH_SCORE_REVIEW_THRESHOLDS.medium) {
    return { key: "medium", label: "Medium Match", pct };
  }
  return { key: "no_match", label: "No Match", pct };
};

const addEvaluation = (index, contactId, campaign = {}) => {
  const id = toId(contactId);
  if (!id) return;

  const campaignId = toId(campaign.campaign_id ?? campaign.campaignId);
  if (!campaignId) return;

  const score = toScore(campaign.match_score ?? campaign.matchScore);
  const entry = {
    campaignId,
    campaignName: campaign.campaign_name ?? campaign.campaignName ?? "",
    score,
    reasoning: campaign.reasoning ?? campaign.reason ?? "",
    error: campaign.error ?? "",
  };

  if (!index.has(id)) index.set(id, new Map());
  index.get(id).set(campaignId, entry);
};

export const createMatchScoreIndex = (response) => {
  const index = new Map();
  const nestedContacts = Array.isArray(response?.contacts)
    ? response.contacts
    : [];

  nestedContacts.forEach((contact) => {
    (Array.isArray(contact?.campaigns) ? contact.campaigns : []).forEach(
      (campaign) => addEvaluation(index, contact.contact_id, campaign),
    );
  });

  // Keep compatibility with older flat bulk responses while the API rolls out
  // the nested response shape.
  if (nestedContacts.length === 0 && Array.isArray(response?.results)) {
    response.results.forEach((campaign) =>
      addEvaluation(index, campaign.contact_id, campaign),
    );
  }

  return index;
};

const isValidEvaluation = (evaluation) =>
  Boolean(evaluation) && evaluation.score !== null && !evaluation.error;

export const buildScoredContacts = (
  contacts = [],
  response,
  campaignId = null,
) => {
  const scoreIndex = createMatchScoreIndex(response);
  const targetCampaignId = campaignId ? toId(campaignId) : null;

  return contacts.map((contact) => {
    const contactId = toId(contact?.id ?? contact?.contact_id ?? contact?.contactId);
    const campaignScores = scoreIndex.get(contactId);
    let evaluation;

    if (targetCampaignId) {
      evaluation = campaignScores?.get(targetCampaignId);
      if (!evaluation) {
        evaluation = {
          campaignId: targetCampaignId,
          error: "No match score was returned for this campaign.",
        };
      }
    } else {
      evaluation = [...(campaignScores?.values() ?? [])]
        .filter(isValidEvaluation)
        .sort((a, b) => b.score - a.score)[0];
      if (!evaluation) {
        evaluation = [...(campaignScores?.values() ?? [])][0] ?? {
          error: "No match score was returned for this lead.",
        };
      }
    }

    const valid = isValidEvaluation(evaluation);
    return {
      ...contact,
      matchScore: valid ? evaluation.score : null,
      matchScoreReason: valid ? evaluation.reasoning : "",
      matchedCampaignName: evaluation?.campaignName ?? "",
      scoreError: valid
        ? ""
        : evaluation?.error || "A valid match score was not generated.",
    };
  });
};

export const applyEmailValidationResults = (contacts = [], response) => {
  const validationByContactId = new Map(
    (Array.isArray(response?.results) ? response.results : []).map((result) => [
      toId(result?.contactId ?? result?.contact_id),
      result,
    ]),
  );

  return contacts.map((contact) => {
    const contactId = toId(contact?.id ?? contact?.contact_id ?? contact?.contactId);
    const result = validationByContactId.get(contactId);
    if (!result) {
      return {
        ...contact,
        emailVerificationStatus: "unknown",
        emailVerificationReason: "Email validation result was not returned.",
        emailVerificationAllowed: false,
      };
    }

    return {
      ...contact,
      emailVerificationStatus: result.status,
      emailVerificationReason: result.reason || "",
      emailVerificationCheckedAt: result.checkedAt || null,
      emailVerificationAllowed: result.allowed !== false,
    };
  });
};
